// ════════════════════════════════════════════════════════════════
// SEED PHRASE GUARD — Detects and blocks BIP39 recovery phrases
// ════════════════════════════════════════════════════════════════
// This module checks if user input looks like a 12 or 24-word BIP39
// seed phrase and blocks it from being saved. This is a safety feature
// that reinforces that the app is NOT designed to store private keys.
// ════════════════════════════════════════════════════════════════

// A subset of common BIP39 words for heuristic detection.
// The full 2048-word list can be loaded from bip39-words.json if
// stronger detection is needed.
const BIP39_COMMON_WORDS = new Set([
  'abandon', 'ability', 'able', 'about', 'above', 'absent', 'absorb', 'abstract',
  'absurd', 'abuse', 'access', 'accident', 'account', 'accuse', 'achieve', 'acid',
  'acoustic', 'acquire', 'across', 'act', 'action', 'actor', 'actress', 'actual',
  'adapt', 'add', 'addict', 'address', 'adjust', 'admit', 'adult', 'advance',
  'advice', 'aerobic', 'affair', 'afford', 'afraid', 'again', 'age', 'agent',
  'agree', 'ahead', 'aim', 'air', 'airport', 'aisle', 'alarm', 'album',
  'alcohol', 'alert', 'alien', 'all', 'alley', 'allow', 'almost', 'alone',
  'alpha', 'already', 'also', 'alter', 'always', 'amateur', 'amazing', 'among',
  'amount', 'amused', 'analyst', 'anchor', 'ancient', 'anger', 'angle', 'angry',
  'animal', 'ankle', 'announce', 'annual', 'another', 'answer', 'antenna', 'antique',
  'anxiety', 'any', 'apart', 'apology', 'appear', 'apple', 'approve', 'april',
  'arch', 'arctic', 'area', 'arena', 'argue', 'arm', 'armed', 'armor',
  'army', 'around', 'arrange', 'arrest', 'arrive', 'arrow', 'art', 'artefact',
  'artist', 'artwork', 'ask', 'aspect', 'assault', 'asset', 'assist', 'assume',
  'asthma', 'athlete', 'atom', 'attack', 'attend', 'attitude', 'attract', 'auction',
  'audit', 'august', 'aunt', 'author', 'auto', 'autumn', 'average', 'avocado',
  'avoid', 'awake', 'aware', 'away', 'awesome', 'awful', 'awkward', 'axis',
  'baby', 'bachelor', 'bacon', 'badge', 'bag', 'balance', 'balcony', 'ball',
  'bamboo', 'banana', 'banner', 'bar', 'barely', 'bargain', 'barrel', 'base',
  'basic', 'basket', 'battle', 'beach', 'bean', 'beauty', 'because', 'become',
  'beef', 'before', 'begin', 'behave', 'behind', 'believe', 'below', 'belt',
  'bench', 'benefit', 'best', 'betray', 'better', 'between', 'beyond', 'bicycle',
  'bid', 'bike', 'bind', 'biology', 'bird', 'birth', 'bitter', 'black',
  'blade', 'blame', 'blanket', 'blast', 'bleak', 'bless', 'blind', 'blood',
  'blossom', 'blouse', 'blue', 'blur', 'blush', 'board', 'boat', 'body',
  'boil', 'bomb', 'bone', 'bonus', 'book', 'boost', 'border', 'boring',
  'borrow', 'boss', 'bottom', 'bounce', 'box', 'boy', 'bracket', 'brain',
  'brand', 'brass', 'brave', 'bread', 'breeze', 'brick', 'bridge', 'brief',
  'bright', 'bring', 'brisk', 'broccoli', 'broken', 'bronze', 'broom', 'brother',
  'brown', 'brush', 'bubble', 'buddy', 'budget', 'buffalo', 'build', 'bulb',
  'bulk', 'bullet', 'bundle', 'bunker', 'burden', 'burger', 'burst', 'bus',
  'business', 'busy', 'butter', 'buyer', 'buzz', 'cabbage', 'cabin', 'cable',
  // ... (first ~400 words of the 2048-word BIP39 list)
  // For production, replace with the full list from:
  // https://github.com/bitcoin/bips/blob/master/bip-0039/english.txt
  'cactus', 'caddy', 'cake', 'calm', 'camera', 'camp', 'can', 'canal',
  'candy', 'cannon', 'canoe', 'canvas', 'canyon', 'capable', 'capital', 'captain',
  'car', 'carbon', 'card', 'cargo', 'carpet', 'carry', 'cart', 'case',
  'cash', 'casino', 'castle', 'casual', 'cat', 'catalog', 'catch', 'category',
  'cattle', 'caught', 'cause', 'caution', 'cave', 'ceiling', 'celery', 'cement',
  'census', 'century', 'cereal', 'certain', 'chair', 'chalk', 'champion', 'change',
  'chaos', 'chapter', 'charge', 'chase', 'chat', 'cheap', 'check', 'cheese',
  'chef', 'cherry', 'chest', 'chicken', 'chief', 'child', 'chimney', 'choice',
  'choose', 'chronic', 'chuckle', 'chunk', 'churn', 'cigar', 'cinnamon', 'circle',
  'citizen', 'city', 'civil', 'claim', 'clap', 'clarify', 'claw', 'clay',
  'clean', 'clerk', 'clever', 'click', 'client', 'cliff', 'climb', 'clinic',
  'clip', 'clock', 'clog', 'close', 'cloth', 'cloud', 'clown', 'club',
  'clump', 'cluster', 'clutch', 'coach', 'coast', 'coconut', 'code', 'coffee',
  'coil', 'coin', 'collect', 'color', 'column', 'combine', 'come', 'comfort',
  'comic', 'common', 'company', 'concert', 'conduct', 'confirm', 'congress', 'connect',
  'consider', 'control', 'convince', 'cook', 'cool', 'copper', 'copy', 'coral',
  'core', 'corn', 'correct', 'cost', 'cotton', 'couch', 'country', 'couple',
  'course', 'cousin', 'cover', 'coyote', 'crack', 'cradle', 'craft', 'cram',
  'crane', 'crash', 'crater', 'crawl', 'crazy', 'cream', 'credit', 'creek',
  'crew', 'cricket', 'crime', 'crisp', 'critic', 'crop', 'cross', 'crouch',
  'crowd', 'crucial', 'cruel', 'cruise', 'crumble', 'crunch', 'crush', 'cry',
  'crystal', 'cube', 'culture', 'cup', 'cupboard', 'curious', 'current', 'curtain',
  'curve', 'cushion', 'custom', 'cycle',
]);

/**
 * Checks if the given text is likely a BIP39 seed phrase.
 * Returns true if the text contains exactly 12 or 24 words and
 * all of them are found in the BIP39 wordlist.
 */
export function isLikelySeedPhrase(text: string): boolean {
  const words = text
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);

  if (words.length !== 12 && words.length !== 24) return false;

  return words.every((word) => BIP39_COMMON_WORDS.has(word));
}

/**
 * Scans all fields of a form for seed phrases.
 * Returns the field key that contains a seed phrase, or null.
 */
export function findSeedPhraseInForm(
  data: Record<string, string>
): string | null {
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string' && isLikelySeedPhrase(value)) {
      return key;
    }
  }
  return null;
}

/**
 * Returns a user-friendly warning message for when a seed phrase is detected.
 */
export function getSeedPhraseWarning(): string {
  return (
    'This looks like a seed phrase or recovery phrase. ' +
    'Cold Vault Ledger does NOT store private keys or recovery phrases. ' +
    'Please use your hardware wallet directly to manage recovery phrases.'
  );
}
